import {
  FlexBoxImageScaleView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import {
  Color,
  ImageScaleView,
  ImageSliceView,
  MultiplyFilter,
  View,
} from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import { startGemsPinchSequence } from 'src/game/shop/Helpers';
import { trackPinchEvent } from 'src/lib/analytics/events';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import { getCreativeSuffix, parseAmount } from 'src/lib/utils';
import * as Encyclopedia from 'src/replicant/encyclopedia';
import * as Inventory from 'src/replicant/inventory';
import { PetSpeciesID, PetEvolutionStage } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import {
  nextSpeciesWeights,
  adultSpecies,
} from 'src/replicant/pet/pet.ruleset';
import StateObserver, { user } from 'src/StateObserver';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;

// true = redo
type CloseHandler = (result: boolean) => void;

export default class PopupRedoEvolution extends PopupBasic {
  private candidatesContainer: FlexBoxImageScaleView;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      skipMessage: true,
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_popup',
      },
      // Add an icon to mush the title to the right
      // This will be hidden by a bigger variant of it to match the mock
      titleIcon: {
        image: 'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
        offsetX: -40,
        width: 60,
        height: 60,
      },
    });

    this.buttonClose.removeFromSuperview();

    const width = this.box.style.width;

    new ImageScaleView({
      superview: this.box,
      autoSize: true,
      image: 'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
      zIndex: 1000,
      x: -10,
      y: -120,
    });

    new LangBitmapFontTextView({
      superview: this.box,
      x: 40,
      y: 80,
      width: width - 80,
      height: 40,
      align: 'center',
      verticalAlign: 'center',
      font: 'Body',
      color: '#BF8147',
      localeText: () => i18n('evolutionRedo.prompt'),
      size: 30,
    });

    this.candidatesContainer = new FlexBoxImageScaleView({
      ...uiConfig.popups.scrollBox,
      superview: this.box,
      x: 30,
      y: 140,
      width: width - 60,
      height: 390,
      flex: {
        justifyContent: 'center',
      },
    });

    new LangBitmapFontTextView({
      superview: this.box,
      x: 44,
      y: 150,
      width: width - 88,
      height: 40,
      verticalAlign: 'center',
      font: 'Body',
      color: '#BF8147',
      localeText: () => i18n('evolutionRedo.candidates'),
      size: 28,
    });

    this.box.addSubview(
      new View({
        backgroundColor: '#BF8147',
        x: 236,
        y: 172,
        width: 280,
        height: 4,
      }),
    );

    new LangBitmapFontTextView({
      superview: this.box,
      x: 50,
      y: 366,
      width: width - 100,
      height: 152,
      align: 'center',
      verticalAlign: 'center',
      font: 'Body',
      color: '#BF8147',
      localeText: () => i18n('evolutionRedo.message'),
      size: 24,
      wordWrap: true,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        y: 124,
        width: width - 80,
      }),
    );

    const costRow = this.box.addSubview(
      new ImageSliceView({
        ...ui.costRow,
        y: 550,
      }),
    );

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('evolutionRedo.requiredGems'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.box.addSubview(
      new ImageSliceView({
        ...ui.balanceRow,
        y: 550 + 68,
      }),
    );

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('evolutionRedo.balanceAfterPurchase'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: 'Body',
      x: width / 2 - 128,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('evolutionRedo.no'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_back',
      },
    });

    const confirm = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: 'Body',
      x: width / 2 + 128,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('evolutionRedo.redo'),
      onClick: async () => {
        sounds.sfx.play('ok');

        if (!this.canPurchase()) {
          trackPinchEvent({
            resource: 'gems',
            trigger: 'redo_evolution',
          });

          await startGemsPinchSequence({
            feature: 'redo_evolution',
            crossplayTracking: {
              feature: 'redo_evolution',
              subFeature: 'redo_evolution',
            },
          });

          this.updateCostAndBalance();
        }

        if (this.canPurchase()) {
          return opts.close(true);
        }
      },
      tracking: { feature: 'redo_evolution', $subFeature: 'redo_evolution_ok' },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/redoEvolution/evo_redo_pocketwatch.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/shop/frames/scroll_cell.png',
      'assets/ui/encyclopedia/frames/outline.png',
    ]);
  }

  public init(opts: { species: PetSpeciesID; stage: PetEvolutionStage }) {
    super.init({
      title: i18n('evolutionRedo.title'),
    });

    const { species, stage } = opts;

    const petSpeciesBucket = Pet.getters.getPetSpeciesBucket(user());

    const candidates =
      stage === 'adult'
        ? this.getInfiniteCandidates(species)
        : (Object.keys(
            nextSpeciesWeights[petSpeciesBucket][species],
          ) as PetSpeciesID[]);

    candidates.forEach((id) => {
      this.candidatesContainer.addSubview(this.createCandidate(id));
    });

    this.updateCostAndBalance();

    if (!this.canPurchase()) {
      this.balance.updateOpts({ color: 'red' });
    }
  }

  private createCandidate(id: PetSpeciesID) {
    const state = Encyclopedia.getters.getSpeciesState(
      StateObserver.getState().user,
      id,
    );
    const frame = new ImageScaleView({
      image: 'assets/ui/shop/frames/scroll_cell.png',
      scaleMethod: '9slice',
      width: 160,
      height: 160,
      sourceSlices: {
        horizontal: { left: 55, right: 55 },
        vertical: { top: 56.5, bottom: 56.5 },
      },
    });

    const node = getFlexNode(frame);
    node.updateStyle({
      flexShrink: 0,
      marginHorizontal: 2,
      marginTop: 60,
    });

    const image = new ImageScaleView({
      superview: frame,
      width: 100,
      height: 100,
      x: frame.style.width * 0.5,
      y: frame.style.height * 0.35,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: `assets/ui/species/linepet_${id}.png`,
    });

    if (state !== 'unlocked') {
      image.setFilter(
        new MultiplyFilter(
          new Color(
            `rgba(255, 255, 255,  ${state === 'unveiled' ? 155 : 255})`,
          ),
        ),
      );
    }

    const petSpeciesBucket = Pet.getters.getPetSpeciesBucket(user());
    const petName = Pet.ruleset.evolutions[petSpeciesBucket][id]?.[
      `name_${getCreativeSuffix()}`
    ].replace('ぶるぶる科', '');

    new ButtonScaleViewWithText({
      superview: frame,
      image: 'assets/ui/encyclopedia/frames/outline.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 15, right: 15 },
        vertical: { top: 15, bottom: 15 },
      },
      font: 'Body',
      textColor: '#9C490D',
      fontSize: 20,
      width: 140,
      height: 30,
      x: frame.style.width * 0.5,
      y: frame.style.height - 35,
      tracking: {
        feature: 'redo_evolution',
        $subFeature: 'redo_evolution_pet',
      },
      localeText: () => `${state === 'veiled' ? '???' : petName}`,
      centerOnOrigin: true,
    });

    return frame;
  }

  private updateCostAndBalance() {
    const user = StateObserver.getState().user;
    this.cost.localeText = () =>
      parseAmount(Pet.getters.getEvolutionRedoCost(user));

    const balance = this.getBalanceAfterRedo();
    this.balance.localeText = () => parseAmount(balance);
  }

  private getBalanceAfterRedo() {
    const user = StateObserver.getState().user;
    return (
      Inventory.getters.getGems(user) - Pet.getters.getEvolutionRedoCost(user)
    );
  }

  private canPurchase() {
    return this.getBalanceAfterRedo() >= 0;
  }

  private getInfiniteCandidates(currentSpecies: PetSpeciesID): PetSpeciesID[] {
    const potentialSpecies = adultSpecies.filter((x) => x !== currentSpecies);

    return potentialSpecies.slice(0, 3);
  }
}
